import {
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';

import {
  Router
} from '@angular/router';

import {
  Subscription
} from 'rxjs';

import {
  MsalService,
  BroadcastService
} from '@azure/msal-angular';

import {
  AuthService,
  GraphService,
  PricelistService,
  PricelistWmsService,
  SidebarActiveState,
  SidebarService,
  ServiceService
} from '../providers';

import {
  getContextPath,
  initDefaultLocales,
  isFrame,
  isIE
} from './app.utils';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  public authReady: boolean;
  public authUser: any;
  public authUserPhoto: string;

  private authSuccessSub: Subscription;
  private authFailureSub: Subscription;
  private authReadySub: Subscription;
  private sidebarActiveStateSub: Subscription;
  private sidebarActiveState: SidebarActiveState = null;

  private expandedListMap = {
    'pricelists': true,
    'cost-lists': true,
    'wms-pricelists': false,
    'wms-cost-lists': false
  };
  
  constructor(
    @Inject(
      'Window'
    ) private window: Window,

    private router: Router,
    private broadcastService: BroadcastService,
    private graphService: GraphService,
    private msalService: MsalService,
    private authService: AuthService,
    private pricelistService: PricelistService,
    private pricelistWmsService: PricelistWmsService,
    private sidebarService: SidebarService,
    private serviceService: ServiceService,
  ) {
    initDefaultLocales();
  }

  public async ngOnInit() {
    if (
      isFrame(
        this.window
      )
    ) {
      return false;
    }

    this.initSubs();
    
    await this.setAuthUser();
    this.setAuthReady();
  }

  public ngOnDestroy() {
    if (this.authSuccessSub) {
      this.authSuccessSub
          .unsubscribe();

      this.authSuccessSub = null;
    }

    if (this.authFailureSub) {
      this.authFailureSub
          .unsubscribe();

      this.authFailureSub = null;
    }

    if (this.authReadySub) {
      this.authReadySub
          .unsubscribe();

      this.authReadySub = null;
    }

    if (this.sidebarActiveStateSub) {
      this.sidebarActiveStateSub
          .unsubscribe();

      this.sidebarActiveStateSub = null;
    }
  }

  public get contextIsLive() {
    const sidebarActiveState = (
      this.getSidebarActiveState()
    );

    if (sidebarActiveState) {
      return (
        sidebarActiveState
          .context ===
        'active'
      );
    }

    return (
      (
        this.router
            .url
            .includes(
              `/${(
                getContextPath(
                  'live'
                )
              )}`
            )
      ) ||
      (
        (
          this.router
              .url
              .split(
                '?'
              )[0]
        ) ===
        '/'
      )
    );
  }

  public get contextIsArchived() {
    const sidebarActiveState = (
      this.getSidebarActiveState()
    );

    if (sidebarActiveState) {
      return (
        sidebarActiveState
          .context ===
        'archived'
      );
    }

    return (
      this.router
          .url
          .includes(
            `/${(
              getContextPath(
                'archived'
              )
            )}`
          )
    );
  }

  public get contextIsRemoved() {
    const sidebarActiveState = (
      this.getSidebarActiveState()
    );

    if (sidebarActiveState) {
      return (
        sidebarActiveState
          .context ===
        'removed'
      );
    }

    return (
      this.router
          .url
          .includes(
            `/${(
              getContextPath(
                'removed'
              )
            )}`
          )
    );
  }

  public get contextIsActive() {
    return (
      this.contextIsLive
    );
  }

  public get domainIsCostLists() {
    const sidebarActiveState = (
      this.getSidebarActiveState()
    );

    if (sidebarActiveState) {
      return (
        sidebarActiveState
          .isCostLists
      );
    }

    return (
      this.router
          .url
          .includes(
            '/cost-lists'
          )
    );
  }
  
  public get pageIsWms() {
    const sidebarActiveState = (
      this.getSidebarActiveState()
    );

    if (sidebarActiveState) {
      return (
        sidebarActiveState
          .isWms
      );
    }

    return (
      this.router
          .url
          .startsWith(
            '/wms'
          )
    );
  }

  public getExpandedIcon(
    key: string
  ) {
    return `${(
      (
        this.isExpanded(
          key
        )
      ) ?
        'minus' :
        'plus'
    )}-square-outline`;
  }

  public isExpanded(
    key: string
  ) {
    return (
      (
        this.expandedListMap[
          key
        ]
      ) ===
      true
    );
  }

  public login() {
    if (isIE) {
      this.msalService
          .loginRedirect({
            extraScopesToConsent: [
              'user.read',
              'openid',
              'profile'
            ]
          });
    }
    else {
      this.msalService
          .loginPopup({
            extraScopesToConsent: [
              'user.read',
              'openid',
              'profile'
            ]
          });
    }
  }

  public logout() {
    this.msalService
        .logout();
  }

  public toggleExpanded(
    key: string
  ) {
    if (
      typeof (
        this.expandedListMap[
          key
        ]
      ) ===
      'boolean'
    ) {
      this.expandedListMap[
        key
      ] = (
        !(
          this.expandedListMap[
            key
          ]
        )
      );
    }
  }

  private initSubs() {
    this.sidebarActiveStateSub = (
      this.sidebarService
          .activeState$
          .subscribe(
            activeState => {
              this.sidebarActiveState = (
                activeState
              );
            }
          )
    );

    this.authSuccessSub = (
      this.broadcastService
          .subscribe(
            'msal:loginSuccess',
            async () => {
              await this.setAuthUser();
              this.setAuthReady();
            }
          )
    );

    this.authFailureSub = (
      this.broadcastService
          .subscribe(
            'msal:loginFailure',
            err => {
              console.warn(
                err.message
              );

              this.authReady = false;
              this.authUser = null;
            }
          )
    );

    this.msalService
        .handleRedirectCallback(
          (
            authError,
            response
          ) => {
            if (authError) {
              console.error(
                'Redirect Error: ',
                (
                  authError
                    .errorMessage
                )
              );

              return;
            }
          }
        );
  }

  private async setAuthUser() {
    this.authUser = (
      this.msalService
          .getAccount()
    );

    this.authUserPhoto = (
      await (
        this.graphService
            .getPhoto()
      )
    );
  }

  private setAuthReady() {
    const value = (
      this.broadcastService
          .getMSALSubject()
          .getValue()
    );

    if (
      value &&
      (
        value
          .payload
      ) &&
      (
        value
          .payload
          .idToken
      )
    ) {
      this.authService
          .authToken = (
            value
              .payload
              .idToken
              .rawIdToken
          );
          
      this.pricelistService
          .authToken = (
            value
              .payload
              .idToken
              .rawIdToken
          );

      this.pricelistWmsService
          .authToken = (
            value
              .payload
              .idToken
              .rawIdToken
          );

      this.serviceService
          .authToken = (
            value
              .payload
              .idToken
              .rawIdToken
          );

      this.authReady = true;
    }
  }

  private getSidebarActiveState(): SidebarActiveState {
    const routerSidebarActiveState = (
      this.getRouterSidebarActiveState()
    );

    if (routerSidebarActiveState) {
      return (
        routerSidebarActiveState
      );
    }

    return (
      this.getUrlIsPricelistRecordForm() ?
        this.sidebarActiveState :
        null
    );
  }

  private getRouterSidebarActiveState(): SidebarActiveState {
    const path = (
      this.router
          .url
          .split(
            '?'
          )[0]
    );

    if (
      [
        '/',
        '/active'
      ].includes(
        path
      )
    ) {
      return {
        context: 'active',
        isCostLists: false,
        isWms: false
      };
    }

    if (
      [
        '/archived',
        '/removed'
      ].includes(
        path
      )
    ) {
      return {
        context: (
          path
            .replace(
              '/',
              ''
            ) as 'archived' | 'removed'
        ),
        isCostLists: false,
        isWms: false
      };
    }

    const pathMatch = (
      path
        .match(
          /^\/(wms\/)?(cost-lists\/)?context\/(active|archived|removed)$/
        )
    );

    if (pathMatch) {
      return {
        context: (
          pathMatch[3] as 'active' | 'archived' | 'removed'
        ),
        isCostLists: (
          !!pathMatch[2]
        ),
        isWms: (
          !!pathMatch[1]
        )
      };
    }

    return (
      null
    );
  }

  private getUrlIsPricelistRecordForm(): boolean {
    const path = (
      this.router
          .url
          .split(
            '?'
          )[0]
    );

    return (
      /^\/(wms\/)?(pricelists|cost-lists)\/[^/]+(\/(clone|markup))?$/
        .test(
          path
        )
    );
  }
}
